import { AutosaveStatus } from '@/core/components/form/AutosaveBoundary'
import { ModalHead, Tabs } from 'admination-react'
import { useEffect, useState, type ReactNode } from 'react'
import { currentVariant, me } from '@/core/data/session'
import { currentAutosaveScope } from '@/core/api/autosaveScope'
import { descriptionFor } from '@/variants'
import { useApp, useCoachingRow, type Modal } from '@/core/state/appStore'
import {
  erstgespraechTabs,
  avgsTabs,
  bewilligungTabs,
  vertragTabs,
  personalTabs,
  ecnTabs,
  abschlussTabs,
  vergabeVerbisTabs,
  vergabeAufnahmePotenzialanalyseTabs,
  vergabeKompetenzfeststellungTabs,
  vergabeKompetenzbilanzierungTabs,
} from '@/core/templates'
import type { Tab, TabOverrides } from '@/core/templates/types'
import { useTabGating } from '@/core/hooks/useTabGating'
import { AUFGABE, tabLabelFor } from '@/core/data/aufgabe'

const SUBTABS: Record<Modal, Tab[]> = {
  erstgespraech: erstgespraechTabs,
  avgs: avgsTabs,
  bewilligung: bewilligungTabs,
  vertrag: vertragTabs,
  personal: personalTabs,
  'invite-coach': [],
  member: [],
  ecn: ecnTabs,

  abschluss: abschlussTabs,
  'create-massnahme': [],
  massnahme: [],
  modul: [],
  'create-modul': [],

  'verbis-lebenslauf': vergabeVerbisTabs,

  'aufnahme-potenzialanalyse': vergabeAufnahmePotenzialanalyseTabs,

  kompetenzfeststellung: vergabeKompetenzfeststellungTabs,
  kompetenzbilanzierung: vergabeKompetenzbilanzierungTabs,

  'teilnehmende-person-anlegen': [],

  dokument: [],

  'dokument-hochladen': [],
}

export function filterAvailable(tabs: Tab[], disabledIds: Set<string>, activeId: string | null): Tab[] {
  return tabs.filter((t) => t.id === activeId || !disabledIds.has(t.id))
}

export type { TabOverrides }

export const STACKS_NARROW = 'data-stack-narrow'
const STACKS_NARROW_MODALS: ReadonlySet<Modal> = new Set(['verbis-lebenslauf', 'kompetenzfeststellung', 'kompetenzbilanzierung'])

/** The active variant's tab-override loader for `modal`, if it has one. */
function variantTabOverrides(modal: Modal): ((coachingId: string) => Promise<TabOverrides>) | undefined {
  let source
  try {
    source = descriptionFor(currentVariant()).tabOverrides
  } catch {
    return undefined
  }
  return source?.modal === modal ? source.load : undefined
}

export function ModalHeader({
  title,
  center,
  size,
  tabs,
  children,
  onClose,
  closeDisabled,
  closeTestId,
}: {
  title?: string
  center?: ReactNode
  size?: 'compact' | 'form' | 'document'
  tabs?: TabOverrides
  children?: ReactNode
  onClose?: () => void
  closeDisabled?: boolean
  closeTestId?: string
}) {
  const { state, dispatch } = useApp()
  const modal = state.screen?.kind as Modal
  const row = useCoachingRow(state.screen?.coachingId ?? null)
  const canGoBack = !!state.history?.length || (modal === 'dokument' && state.screen?.returnTo === 'documents') || modal === 'dokument-hochladen'
  const [completion, setCompletion] = useState<{ identity: string; tabs: string[] }>({ identity: '', tabs: [] })
  const completionIdentity = JSON.stringify([state.screen?.coachingId, modal, state.screen?.tab, state.dashboardVersion, currentAutosaveScope()])
  useEffect(() => {
    if (!state.screen?.coachingId) return
    let cancelled = false
    const coachingId = state.screen?.coachingId
    void (async () => {
      try {
        const session = await me()
        if (cancelled || !session.variant) return
        const load = descriptionFor(session.variant).completedTabs
        if (!load) return
        const tabs = await load(coachingId, modal)
        if (!cancelled) setCompletion({ identity: completionIdentity, tabs })
      } catch (error) {
        if (!cancelled) console.error('Abgeschlossene Schritte konnten nicht geladen werden:', error)
      }
    })()
    return () => { cancelled = true }
  }, [completionIdentity, state.screen?.coachingId, modal])
  const completedTabs = completion.identity === completionIdentity ? completion.tabs : []
  const overrideSource = variantTabOverrides(modal)
  const overrideKey = `${state.screen?.coachingId}|${modal}`
  const [variantTabs, setVariantTabs] = useState<{ key: string; tabs: TabOverrides } | null>(null)
  useEffect(() => {
    if (!state.screen?.coachingId || !overrideSource) return
    let cancelled = false
    const key = `${state.screen?.coachingId}|${modal}`
    overrideSource(state.screen?.coachingId)
      .then((tabs) => { if (!cancelled) setVariantTabs({ key, tabs }) })
      .catch((error) => console.error('Schritte konnten nicht geladen werden:', error))
    return () => { cancelled = true }
  }, [completionIdentity, state.screen?.coachingId, modal, overrideSource])
  const resolvedTabs = variantTabs?.key === overrideKey ? variantTabs.tabs : undefined
  const overridesPending = !!overrideSource && !resolvedTabs
  const { disabledSubTabIds, aufgabeName, position } = useTabGating()

  const shownSubTabs = filterAvailable(SUBTABS[modal], disabledSubTabIds, state.screen?.tab ?? null).flatMap((t) => {
    if (modal === 'personal' && t.id === 'anwesenheit' && !descriptionFor(currentVariant()).anwesenheit) return []
    const { hidden, ...override } = { ...resolvedTabs?.[t.id], ...tabs?.[t.id] }
    if (t.id !== state.screen?.tab && (hidden || overridesPending)) return []
    return [{
      ...t,
      label: tabLabelFor(modal, t.id, aufgabeName) ?? t.label,
      ...override,
    }]
  })

  return (
    <ModalHead
      {...(STACKS_NARROW_MODALS.has(modal) ? { [STACKS_NARROW]: '' } : {})}
      modalSize={size}
      modalLabel={[row?.name, title ?? shownSubTabs.find((tab) => tab.id === state.screen?.tab)?.label].filter(Boolean).join(' - ') || 'Dialog'}
      leftProps={{ 'data-tab-bar': true }}
      left={row?.name ? {
        type: 'title', label: row.name, icon: 'i-person', testId: 'teilnehmer-name', active: false,
        ...(modal !== 'personal' && { onClick: () => { if (!closeDisabled) dispatch({ type: 'CHANGE_PHASE', target: { modal: 'personal', tab: 'uebersicht' }, remember: true }) } }),
      } : undefined}
      center={<>
        {shownSubTabs.length > 0
          ? <Tabs items={shownSubTabs.map((t) => ({
              name: t.id, label: t.label, icon: t.icon, disabled: closeDisabled,
              completed: t.completed ?? (completedTabs.includes(t.id) || (position != null && Object.values(AUFGABE).some((task) => task.landing?.modal === modal && task.landing.tab === t.id)
                && Object.values(AUFGABE).filter((task) => task.landing?.modal === modal && task.landing.tab === t.id).every((task) => task.position < position))),
            }))} activeName={state.screen?.tab ?? ''} onChange={(tab) => { if (!closeDisabled) dispatch({ type: "CHANGE_TAB", tab }) }} />
          : title && <span className="bar-title" data-testid="dokument-titel">{title}</span>}
        {center}
      </>}
      actions={<><AutosaveStatus />{children}</>}
      onBack={canGoBack ? () => dispatch({ type: 'GO_BACK' }) : undefined}
      backLabel="Zurück"
      onClose={onClose ?? (() => dispatch({ type: 'DISMISS' }))}
      closeLabel="Schließen"
      closeDisabled={closeDisabled}
      closeTestId={closeTestId}
    />
  )
}
